import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
const SLIDES = [{
  title: 'Event Planning',
  description: 'Custom support for birthdays, showers, weddings, private celebrations, vendor events, and corporate gatherings.',
  image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=2069&auto=format&fit=crop',
  tag: 'Strategic Planning'
}, {
  title: 'Event Design & Styling',
  description: 'Theme development, color coordination, layout planning, décor styling, and visual presentation.',
  image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=2074&auto=format&fit=crop',
  tag: 'Creative Vision'
}, {
  title: 'Event Rentals',
  description: 'Tables, chairs, linens, backdrops, balloon décor, props, signage, displays, and specialty rental items.',
  image: 'https://cdn.magicpatterns.com/uploads/id942KqazF6YEA53f7Kv4s/pexels-roger-ce-311041755-15477351.jpg',
  tag: 'Premium Inventory'
}, {
  title: 'Setup & Breakdown',
  description: 'Delivery, setup, styling, and post-event breakdown for a smoother event experience.',
  image: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?q=80&w=2162&auto=format&fit=crop',
  tag: 'Seamless Execution'
}];
export function Hero() {
  const [current, setCurrent] = useState(0);
  const next = () => setCurrent(prev => (prev + 1) % SLIDES.length);
  const prev = () => setCurrent(p => (p - 1 + SLIDES.length) % SLIDES.length);
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, []);
  return <section className="relative h-[85vh] w-full overflow-hidden bg-black" data-id="element-34">
      {/* Background Image with Crossfade + Zoom Effect */}
      <AnimatePresence mode="wait" data-id="element-35">
        <motion.div key={current} initial={{
        opacity: 0,
        scale: 1.1
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        opacity: {
          duration: 1
        },
        scale: {
          duration: 8,
          ease: 'easeOut'
        }
      }} className="absolute inset-0" data-id="element-36">
          <img src={SLIDES[current].image} alt={SLIDES[current].title} className="w-full h-full object-cover opacity-60" data-id="element-37" />
        </motion.div>
      </AnimatePresence>

      {/* Hero Content */}
      <div className="relative h-full max-w-[1440px] mx-auto px-6 flex flex-col justify-center items-center text-center text-white z-10" data-id="element-38">
        <motion.span initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="text-[12px] md:text-[14px] uppercase tracking-[0.4em] font-bold mb-6 block" data-id="element-39">
          {SLIDES[current].tag}
        </motion.span>

        <motion.h1 key={`title-${current}`} initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="font-serif text-5xl md:text-7xl lg:text-8xl mb-8 leading-[1.1]" data-id="element-40">
          {SLIDES[current].title.split(' ')[0]}{' '}
          <span className="italic text-white" data-id="element-41">
            {SLIDES[current].title.split(' ').slice(1).join(' ')}
          </span>
        </motion.h1>

        <motion.div key={`desc-${current}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="text-lg md:text-xl max-w-2xl mb-12 font-light tracking-wide leading-relaxed" data-id="element-42">
          {SLIDES[current].description}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="flex flex-col sm:flex-row gap-6" data-id="element-43">
          <Link to="/contact" data-id="element-44">
            <button className="bg-white text-black px-10 py-5 text-sm font-bold uppercase tracking-widest hover:bg-[#b22222] hover:text-white transition-all duration-300 flex items-center gap-3 group" data-id="element-45">
              Book a Consultation
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-46" />
            </button>
          </Link>
        </motion.div>

        {/* Dots */}
        <div className="flex gap-3 mt-16" data-id="element-47">
          {SLIDES.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className="group py-2" data-id="element-48">
              <div className={`h-[2px] transition-all duration-500 ${i === current ? 'w-10 bg-white' : 'w-5 bg-white/30 group-hover:bg-white/60'}`} data-id="element-49" />
            </button>)}
        </div>
      </div>

      {/* Side Navigation Arrows */}
      <div className="absolute inset-y-0 left-6 flex items-center z-30" data-id="element-50">
        <button onClick={prev} className="p-4 border border-white/10 bg-black/10 backdrop-blur-sm text-white hover:bg-white hover:text-black transition-all duration-300 rounded-full group" data-id="element-51">
          <ChevronLeft size={24} className="group-hover:-translate-x-0.5 transition-transform" data-id="element-52" />
        </button>
      </div>
      <div className="absolute inset-y-0 right-6 flex items-center z-30" data-id="element-53">
        <button onClick={next} className="p-4 border border-white/10 bg-black/10 backdrop-blur-sm text-white hover:bg-white hover:text-black transition-all duration-300 rounded-full group" data-id="element-54">
          <ChevronRight size={24} className="group-hover:translate-x-0.5 transition-transform" data-id="element-55" />
        </button>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-10 hidden lg:flex flex-col items-center gap-2" data-id="element-56">
        <div className="w-[1px] h-12 bg-white/30 relative overflow-hidden" data-id="element-57">
          <motion.div animate={{
          y: [0, 48]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute top-0 left-0 w-full h-1/2 bg-white" data-id="element-58" />
        </div>
      </div>
    </section>;
}